import React, { FC, Fragment, isValidElement, useCallback, useEffect } from 'react';
import { Portal } from '@radix-ui/react-portal';
import { ESCAPE_KEY } from 'constants/keyCodes';
import noop from 'utils/noop';
import { TakeOverTestIds } from './TakeOver.constants';
import { TakeOverContainer, TakeOverPanel } from './TakeOver.styled';
import { PanelBody } from './Panel';
import { TakeOverProps } from './TakeOver.types';

/**
 * The Take Over component has different atomic sections with properties that can be applied based on the context and location of the Take Over.
 * Use this component (instead of Drawer/FullScreenTakeover) when you want full control over the Take Over's content and layout.
 */
export const TakeOver: FC<React.PropsWithChildren<TakeOverProps>> = ({
  onClose = noop,
  Header,
  Footer,
  NavBar,
  Overlay,
  children,
  withPortal = false,
  className = '',
  size,
  ...props
}): JSX.Element => {
  const handleKeyDown = useCallback(
    (e: any) => {
      const { keyCode } = e;

      // escape key pressed
      if (keyCode === ESCAPE_KEY) {
        onClose(e);
      }
    },
    [onClose],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const PortalWrapper = withPortal ? Portal : Fragment;

  return (
    <PortalWrapper {...(withPortal && { asChild: true })}>
      <TakeOverContainer
        data-test={TakeOverTestIds.component}
        id={TakeOverTestIds.component}
        className={className}
        {...props}
      >
        {isValidElement(Overlay) && Overlay}
        {isValidElement(NavBar) && NavBar}
        <TakeOverPanel data-test={TakeOverTestIds.main} size={size}>
          {isValidElement(Header) && Header}
          <PanelBody>{children}</PanelBody>
          {isValidElement(Footer) && Footer}
        </TakeOverPanel>
      </TakeOverContainer>
    </PortalWrapper>
  );
};
